import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import { array, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { or, and, not, eq } from 'ember-truth-helpers';
import { variation } from 'ember-launch-darkly';
import can from 'ember-can/helpers/can';
import {
  HdsPageHeader,
  HdsLinkInline,
  HdsButton,
  HdsTable,
  HdsIcon,
  HdsTextBody,
  HdsBadge,
  HdsTooltipButton,
  HdsDropdown,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  Flex,
  Pagination,
} from 'core/components';
import { boxMargin, boxPadding } from 'core/modifiers';
import { optionForSourceState, listIncludes, rbacRoles } from 'core/helpers';
import Remove from 'hcp/components/page/users/remove';

/**
 * `model` has the list of HCP users, the current user, and the organization.
 * @argument model
 * @type {Object}
 */
/**
 *
 * `PageUsersList` lists the HCP Users.
 *
 *
 * ```
 * <Page::Users::List @model={{model}} />
 * ```
 *
 * @class PageUsersList
 *
 */
export default class PageUsersList extends Component {
  @service abilities;
  @service currentUser;
  @service flashMessages;
  @service intl;
  @service router;

  @tracked showRemoveModal = false;
  @tracked usersToRemove = null;

  canDeleteUser = (user) => {
    const { orgPolicy } = this.args.model;
    return this.abilities.can('delete user', user, {
      orgPolicy: orgPolicy,
    });
  };

  get canUpdateUser() {
    return this.abilities.can('set-iam-policy in organization');
  }

  canViewDropdown = (user) => {
    return this.canDeleteUser(user) || this.canUpdateUser;
  };

  isScimManaged = (user) => {
    const { scimEnabled } = this.args.model;
    return scimEnabled && user.scimSynchronized;
  };

  shouldShowSeparator = (user) => {
    // only show the HDS separator if multiple menu items are present based on the user's abilities
    return (
      (this.canUpdateUser && this.isScimManaged(user)) ||
      (this.canUpdateUser && this.canDeleteUser(user))
    );
  };

  confirmRemoveUsers = (users) => {
    this.showRemoveModal = true;
    this.usersToRemove = users;
  };

  onRemoveUsersError = ({ users }) => {
    let action = 'remove';
    if (
      users[0].identityTypes.includes('SAMLP') ||
      users[0].identityTypes.includes('HASHICORP_SSO')
    ) {
      action = 'delete';
    }
    this.flashMessages.error(
      this.intl.t(
        `components.page.access-control.users.${action}.error.title`,
        {
          numUsers: users.length,
        }
      ),
      {
        actionText: this.intl.t(
          `components.page.access-control.users.${action}.error.action`
        ),
        content: this.intl.t(
          `components.page.access-control.users.${action}.error.content`,
          {
            numUsers: users.length,
          }
        ),
        onAction: (close) => {
          window.location.href =
            'https://support.hashicorp.com/hc/en-us/requests/new';
          return close();
        },
      }
    );

    return;
  };

  onRemoveUsersSuccess = ({ users }) => {
    this.resetRemoveUsers();
    // We should use the router service for this once this PR is merged.
    // via: https://github.com/emberjs/rfcs/issues/592
    let route = getOwner(this).lookup(`route:${this.router.currentRouteName}`);
    let action = 'remove';
    if (
      users[0].identityTypes.includes('SAMLP') ||
      users[0].identityTypes.includes('HASHICORP_SSO')
    ) {
      action = 'delete';
    }
    this.flashMessages.success(
      this.intl.t(`components.page.access-control.users.${action}.success`, {
        numUsers: users.length,
      })
    );
    return route.refresh();
  };

  resetRemoveUsers = () => {
    this.showRemoveModal = false;
    this.usersToRemove = null;
  };

  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          <PH.IconTile @icon="user" />
          <PH.Title>
            {{t "components.page.access-control.users.list.title"}}
          </PH.Title>
          <PH.Description>
            {{t "components.page.access-control.users.list.description"}}
            <HdsLinkInline
              class="hds-typography-body-200"
              @color="secondary"
              @href="https://developer.hashicorp.com/hcp/docs/hcp/admin/iam/users#user-permissions"
              @icon="external-link"
              data-test-organization-overview-learn-more
            >
              {{t "components.page.access-control.users.list.learn-more"}}
            </HdsLinkInline>
          </PH.Description>
          <PH.Actions>
            {{#if
              (can "create invitation" undefined ssoEnabled=@model.ssoEnabled)
            }}
              <HdsButton
                @color="primary"
                @icon="plus"
                @iconPosition="leading"
                @route="cloud.access-control.users.invite"
                @text={{t "components.page.access-control.users.invite.action"}}
                data-test-users-create
              />
            {{/if}}
          </PH.Actions>
        </HdsPageHeader>
      </:header>
      <:body>
        <section>
          <HdsTable
            @caption={{t "components.page.access-control.users.list.title"}}
            @valign="top"
          >
            <:head as |H|>
              <H.Tr>
                <H.Th>
                  {{t
                    "components.page.access-control.users.list.table.headers.name"
                  }}
                </H.Th>
                <H.Th>
                  {{t
                    "components.page.access-control.users.list.table.headers.email"
                  }}
                </H.Th>
                {{#if @model.orgPolicy}}
                  <H.Th>
                    {{t
                      "components.page.access-control.users.list.table.headers.org-role"
                    }}
                  </H.Th>
                {{/if}}
                <H.Th>
                  {{t
                    "components.page.access-control.users.list.table.headers.method"
                  }}
                </H.Th>
                {{#if
                  (and
                    (eq @model.ssoEnabled true)
                    (variation "hcp-identity-scim-enabled")
                  )
                }}
                  <H.Th>
                    {{t
                      "components.page.access-control.users.list.table.headers.scim-provisioning"
                    }}
                  </H.Th>
                {{/if}}
                <H.Th @align="right" @width="83px">
                  {{t
                    "components.page.access-control.users.list.table.headers.actions"
                  }}
                </H.Th>
              </H.Tr>
            </:head>
            <:body as |B|>
              {{#each @model.userPrincipals key="id" as |user|}}
                <B.Tr data-test-user-item>
                  <B.Td data-test-user-item-name>
                    {{#if @model.orgPolicy}}
                      <HdsLinkInline
                        @color="secondary"
                        @model={{user.id}}
                        @route="cloud.access-control.users.detail"
                        class="hds-font-weight-medium"
                        data-test-user-item-full-name
                        data-test-user-item-full-name-link
                      >
                        {{or user.fullName user.email}}
                      </HdsLinkInline>
                    {{else}}
                      <span
                        data-test-user-item-full-name
                        data-test-user-item-full-name-label
                      >
                        {{or user.fullName user.email}}
                      </span>
                    {{/if}}
                    {{#if (eq this.currentUser.user.id user.id)}}
                      <span
                        class="hds-typography-body-100 hds-font-weight-regular hds-foreground-faint"
                        data-test-user-item-is-you-label
                      >
                        ({{t
                          "components.page.access-control.users.list.table.labels.current-user"
                        }})
                      </span>
                    {{/if}}
                  </B.Td>
                  <B.Td data-test-user-item-email>
                    {{user.email}}
                  </B.Td>
                  {{#if @model.orgPolicy}}
                    <B.Td>
                      <ol class="unstyled">
                        {{#each user.roleLocations as |roleLocation index|}}
                          {{#let
                            (rbacRoles
                              roleLocation.roleId
                              active=true
                              type=roleLocation.link.type
                            )
                            as |rbacRole|
                          }}
                            <li
                              {{boxMargin (if (eq index 0) "0" "xs 0 0 0")}}
                              data-test-user-item-role-id={{roleLocation.roleId}}
                            >
                              {{or
                                rbacRole.label
                                roleLocation.title
                                roleLocation.roleId
                                "—"
                              }}
                            </li>
                          {{/let}}
                        {{else}}
                          <li data-test-user-item-role-id>
                            —
                          </li>
                        {{/each}}
                      </ol>
                    </B.Td>
                  {{/if}}
                  <B.Td data-test-user-item-method>
                    <Flex @alignItems="center" @direction="row" @gap="md">
                      {{#let
                        (or user.identityTypes (array user.identityType))
                        as |identityTypes|
                      }}
                        {{#each identityTypes as |type|}}
                          {{#let
                            (optionForSourceState "identity_types" type)
                            as |option|
                          }}
                            <Flex
                              @alignItems="center"
                              @direction="row"
                              @gap="2xs"
                              data-test-user-item-identity-type-badge
                            >
                              <HdsIcon
                                @color={{option.iconColor}}
                                @name={{option.icon}}
                                @isInline={{true}}
                              />
                              <span>
                                {{t option.text}}
                              </span>
                            </Flex>
                          {{/let}}
                        {{/each}}
                      {{/let}}
                    </Flex>
                  </B.Td>
                  {{#if
                    (and
                      (eq @model.ssoEnabled true)
                      (variation "hcp-identity-scim-enabled")
                    )
                  }}
                    <B.Td data-test-user-item-scim-provisioning>
                      <Flex @alignItems="center" @direction="row" @gap="md">
                        {{#let
                          (and
                            @model.scimEnabled (eq user.scimSynchronized true)
                          )
                          (and
                            @model.scimEnabled (eq user.scimSynchronized false)
                          )
                          (not
                            (listIncludes
                              user.identityTypes "SAMLP" "HASHICORP_SSO"
                            )
                          )
                          (eq @model.scimEnabled false)
                          as |syncing createdViaSso nonSsoUser notEnabled|
                        }}
                          {{#if nonSsoUser}}

                            <HdsTextBody
                              @color="faint"
                              @size="200"
                              @weight="regular"
                              data-test-user-item-scim-provisioning-non-sso-user
                            >
                              {{t
                                "components.page.access-control.users.list.table.scim-provisioning.non-sso-user"
                              }}
                            </HdsTextBody>
                          {{else if (or syncing notEnabled)}}
                            <HdsBadge
                              @text={{if
                                syncing
                                (t
                                  "components.page.access-control.users.list.table.scim-provisioning.syncing"
                                )
                                (t
                                  "components.page.access-control.users.list.table.scim-provisioning.not-enabled"
                                )
                              }}
                              @type="outlined"
                              @icon={{if syncing "check" "x"}}
                              @color={{if syncing "success"}}
                              data-test-user-item-scim-provisioning-badge
                            />
                          {{else}}
                            <Flex
                              @alignItems="center"
                              @direction="row"
                              @gap="sm"
                            >
                              <HdsTextBody
                                @color="faint"
                                @size="200"
                                @weight="regular"
                                data-test-user-item-scim-provisioning-created-via-sso
                              >
                                {{t
                                  "components.page.access-control.users.list.table.scim-provisioning.created-via-sso"
                                }}
                              </HdsTextBody>
                              <HdsTooltipButton
                                @text={{t
                                  "components.page.access-control.users.list.table.scim-provisioning.created-via-sso-tooltip"
                                }}
                                @placement="bottom"
                                aria-label={{t
                                  "components.page.access-control.users.list.table.scim-provisioning.created-via-sso-tooltip"
                                }}
                                data-test-user-item-scim-provisioning-created-via-sso-tooltip
                              >
                                <HdsIcon
                                  @name="alert-circle"
                                  @color="var(--token-color-foreground-faint)"
                                  @isInline={{true}}
                                />
                              </HdsTooltipButton>
                            </Flex>
                          {{/if}}
                        {{/let}}
                      </Flex>
                    </B.Td>
                  {{/if}}
                  <B.Td>
                    {{#let
                      (can "ssoDelete user" user)
                      as |shouldAllowSsoUserDeletion|
                    }}
                      {{#if (this.canViewDropdown user)}}
                        <Flex @justifyContent="end">
                          <HdsDropdown data-test-more-menu as |dd|>
                            <dd.ToggleIcon
                              @icon="more-horizontal"
                              @text={{t "components.form.more"}}
                              @hasChevron={{false}}
                              data-test-user-item-menu
                              data-test-more-menu-trigger
                            />
                            {{#if this.canUpdateUser}}
                              <dd.Interactive
                                @icon="edit"
                                @model={{user.id}}
                                @route="cloud.access-control.role-assignments.detail.edit"
                                @text={{t
                                  "components.page.access-control.users.list.table.actions.edit-role-assignments"
                                }}
                                data-test-edit-user-button
                              />
                            {{/if}}
                            {{#if (this.shouldShowSeparator user)}}
                              <dd.Separator />
                            {{/if}}
                            {{#if (this.isScimManaged user)}}
                              <dd.Generic {{boxPadding "xs 0 xs md"}}>
                                <HdsTextBody
                                  @color="primary"
                                  @size="200"
                                  data-test-scim-manage-user-text
                                >
                                  {{t
                                    "components.page.access-control.users.list.table.actions.manage-scim-user"
                                  }}
                                </HdsTextBody>
                              </dd.Generic>
                            {{else if (this.canDeleteUser user)}}
                              <dd.Interactive
                                @color="critical"
                                @icon={{if
                                  shouldAllowSsoUserDeletion
                                  "trash"
                                  "minus-circle"
                                }}
                                @text={{if
                                  shouldAllowSsoUserDeletion
                                  (t
                                    "components.page.access-control.users.list.table.actions.delete"
                                  )
                                  (t
                                    "components.page.access-control.users.list.table.actions.remove"
                                  )
                                }}
                                data-test-remove-user-button
                                {{on
                                  "click"
                                  (fn this.confirmRemoveUsers (array user))
                                }}
                              />
                            {{/if}}
                          </HdsDropdown>
                        </Flex>
                      {{/if}}
                    {{/let}}
                  </B.Td>
                </B.Tr>
              {{/each}}
            </:body>
          </HdsTable>
          <Pagination @pagination={{@model.pagination}} />
        </section>
        <Remove
          @isActive={{this.showRemoveModal}}
          @organization={{@model.organization}}
          @onActiveChange={{this.resetRemoveUsers}}
          @onError={{this.onRemoveUsersError}}
          @onSuccess={{this.onRemoveUsersSuccess}}
          @policy={{@model.orgPolicy}}
          @users={{this.usersToRemove}}
        />
      </:body>
    </CoreLayoutMain>
  </template>
}
